/**
 * The project as a sequence of events: what arrived, what was offered, what was signed, what was
 * released, oldest first.
 *
 * THE SOURCE PRODUCT'S TIMELINE VIEW, seen 2026-10-27: one row per event across the whole folder
 * -- quote request received, quote response released, quote response accepted, contract issued --
 * each with its date, a dot coloured by kind, the record's number and one detail line. Nothing on
 * it is a new fact. Every row here is read from something the estate already records:
 *
 *   * a record's AUDIT HISTORY (object_audit_histories?ObjectUSID=): its creation, and every
 *     status transition, as a Change entry whose Field Path is Status;
 *   * a quote request's RECEIVED time and subject, from its satellite;
 *   * an offer's ACCEPTANCES (commercial_acceptances?PreparedQuoteRevisionUSID=): the customer's
 *     side and the countersignature, each with who signed, and a withdrawal if there was one.
 *
 * ONE READ PER MEMBER, plus one per offer for its acceptances. The folder's membership list says
 * what to read; nothing is scanned.
 *
 * THE DATABASE WRITES UTC WITHOUT SAYING SO, and every timestamp here is read as UTC. See
 * QuoteRequestPane.
 */
import ObjectIdLink from "./ObjectIdLink";
import React, {useEffect, useMemo, useState} from "react";
import {Box, CircularProgress, Typography} from "@mui/material";

import {Blue3, Grey2, Grey6, Grey7, Grey8, Grey17} from "../../theme/styles";
import type {PipelineMember} from "./ProductPipeline";

type Props = {api: any; members: PipelineMember[]};

type Event = {
    at: Date;
    kind: string;
    label: string;
    id?: string | null;
    detail?: string | null;
    who?: string | null;
    revisionUSID?: string | null;
    key: string;
};

const KIND_WORD: Record<string, string> = {
    QuoteRequest: "Quote request", PreparedQuote: "Quote response", Contract: "Contract",
    BuildPlan: "Build plan", Product: "Product",
};
const KIND_COLOUR: Record<string, string> = {
    QuoteRequest: "#f59e0b", PreparedQuote: "#10b981", Contract: "#0d9488", BuildPlan: Blue3,
    Product: Grey6,
};
const STATUS_WORD: Record<string, string> = {
    RELEASED: "released", DEPRECATED: "retired", CANCELLED: "cancelled", DRAFT: "revised",
    OBSOLETE: "made obsolete",
    // A project's two states.
    CLOSED: "closed", OPEN: "reopened",
};

const listOf = <T,>(held: T[] | T | undefined | null): T[] =>
    Array.isArray(held) ? held : (held ? [held] : []);

function asUtc(value?: string | null): Date | null {
    if (!value) return null;
    const text = String(value).replace(" ", "T");
    const at = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(text) ? text : `${text}Z`);
    return Number.isNaN(at.getTime()) ? null : at;
}

/** The audit row's Change, which arrives base64-encoded JSON, plain JSON, or already parsed. */
function changesOf(raw: any): any[] {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    if (typeof raw !== "string") return [];
    try { return JSON.parse(atob(raw)); } catch {
        try { return JSON.parse(raw); } catch { return []; }
    }
}

function Row({event}: {event: Event}) {
    const date = event.at.toLocaleDateString(undefined, {month: "short", day: "numeric",
                                                         year: "numeric"});
    const time = event.at.toLocaleTimeString(undefined, {hour: "numeric", minute: "2-digit"});
    return (
        <Box sx={{display: "grid", gridTemplateColumns: "92px 20px 1fr", gap: "10px",
                  alignItems: "start", padding: "8px 0"}}>
            <Box sx={{textAlign: "right", paddingTop: "10px"}}>
                <Typography sx={{fontSize: 11, color: Grey7}}>{date}</Typography>
                <Typography sx={{fontSize: 10.5, color: Grey6}}>{time}</Typography>
            </Box>
            <Box sx={{display: "flex", justifyContent: "center", paddingTop: "12px"}}>
                <Box sx={{width: 10, height: 10, borderRadius: "50%",
                          background: KIND_COLOUR[event.kind] ?? Grey6}}/>
            </Box>
            <Box sx={{border: `1px solid ${Grey2}`, borderRadius: "6px", padding: "8px 12px"}}>
                <Typography sx={{fontSize: 10.5, color: KIND_COLOUR[event.kind] ?? Grey7,
                                 textTransform: "uppercase", letterSpacing: "0.05em",
                                 fontWeight: 700}}>
                    {event.label}
                </Typography>
                <ObjectIdLink id={event.id} revisionUSID={event.revisionUSID}/>
                {(event.detail || event.who) && (
                    <Typography sx={{fontSize: 12, color: Grey8}}>
                        {event.detail}{event.detail && event.who ? " · " : ""}{event.who}
                    </Typography>
                )}
            </Box>
        </Box>
    );
}

export default function ProjectTimeline({api, members}: Props) {
    const [byMember, setByMember] = useState<Record<string, Event[]>>({});
    const [failed, setFailed] = useState<string[]>([]);
    const [pending, setPending] = useState(0);

    const memberKey = members.map(one => `${one.MemberUSID}:${one.ObjectID ?? ""}`).join("|");

    useEffect(() => {
        let live = true;
        const visible = members.filter(one => one.visible !== false);
        setPending(visible.length);
        setFailed([]);

        const headers = (idoc: string) => ({headers: {"idoc-response": idoc}});

        const auditEvents = (member: PipelineMember) => api
            .get("t1/v1/object_audit_histories",
                 {params: {ObjectUSID: member.MemberUSID, Limit: 1000},
                  ...headers("v1.ObjectAuditHistory-001")})
            .then((answer: any) => {
                const rows = listOf<any>(answer?.data?.PlatformMessage?.ListOfObjectAuditHistory
                                             ?.ObjectAuditHistory)
                    .map(row => ({...row, when: asUtc(row.CreatedDatetime)}))
                    .filter(row => row.when)
                    .sort((a, b) => a.when.getTime() - b.when.getTime());
                const word = KIND_WORD[member.MemberType] ?? member.MemberType;
                const out: Event[] = [];
                rows.forEach((row, index) => {
                    if (index === 0) {
                        // THE FIRST ROW IS THE RECORD'S BIRTH, and ONLY that: its change list
                        // carries every field going from nothing to its first value, Status to
                        // DRAFT among them, and read as a transition that drew "revised" beside
                        // every "received" on the first real timeline. A request's birth is its
                        // receipt, which the satellite dates more honestly (the mail may predate
                        // the row), so for a request this row contributes nothing.
                        if (member.MemberType !== "QuoteRequest") {
                            out.push({at: row.when, kind: member.MemberType,
                                      label: `${word} created`, id: member.ObjectID,
                                      detail: member.Name, who: row.CreatedByName,
                                      revisionUSID: row.ObjectRevisionUSID ?? member.RevisionUSID,
                                      key: `${member.MemberUSID}:created`});
                        }
                        return;
                    }
                    const status = changesOf(row.Change).find(
                        change => /^status$/i.test(String(change?.["Field Path"] ?? "")));
                    // A TRANSITION HAS A PREVIOUS VALUE. One without is a field being set for the
                    // first time, which is creation under another name.
                    if (status && status["Next Value"] && status["Previous Value"]) {
                        const next = String(status["Next Value"]).toUpperCase();
                        const verb = STATUS_WORD[next] ?? next.toLowerCase();
                        out.push({at: row.when, kind: member.MemberType,
                                  label: member.MemberType === "Contract" && next === "RELEASED"
                                      ? "Contract issued" : `${word} ${verb}`,
                                  id: member.ObjectID, detail: member.Name, who: row.CreatedByName,
                                  revisionUSID: row.ObjectRevisionUSID ?? member.RevisionUSID,
                                  key: `${member.MemberUSID}:${row.USID}`});
                    }
                });
                return out;
            });

        const requestEvents = (member: PipelineMember) => api
            .get("t1/v1/quote_requests", {params: {ObjectUSID: member.MemberUSID},
                                          ...headers("v1.QuoteRequest-001")})
            .then((listed: any) => {
                const satellite = listOf<any>(listed?.data?.PlatformMessage?.ListOfQuoteRequest
                                                  ?.QuoteRequest)[0];
                if (!satellite?.USID) return [] as Event[];
                return api.get("t1/v1/quote_requests", {params: {USID: satellite.USID},
                                                        ...headers("v1.QuoteRequest-001")})
                    .then((got: any) => {
                        const one = got?.data?.PlatformMessage?.QuoteRequest ?? satellite;
                        const out: Event[] = [];
                        const received = asUtc(one.ReceivedDatetime);
                        if (received) {
                            out.push({at: received, kind: "QuoteRequest",
                                      label: "Quote request received", id: member.ObjectID,
                                      detail: one.Subject || one.RequestedProduct || member.Name,
                                      who: one.RequesterName,
                                      revisionUSID: member.RevisionUSID,
                                      key: `${member.MemberUSID}:received`});
                        }
                        // THE OFFERS' ACCEPTANCES, reached through the request so an offer not
                        // filed here itself is still dated by the request it answers.
                        const offers = listOf<any>(one?.ListOfPreparedQuote?.PreparedQuote);
                        return Promise.all(offers.map(offer => api
                            .get("t1/v1/commercial_acceptances",
                                 {params: {PreparedQuoteRevisionUSID: offer.PreparedQuoteRevisionUSID,
                                           IncludeWithdrawn: "true"}})
                            .then((acc: any) => listOf<any>(acc?.data?.PlatformMessage
                                ?.ListOfCommercialAcceptance?.CommercialAcceptance))
                            .catch(() => [] as any[])
                            .then((rows: any[]) => rows.flatMap((row: any) => {
                                const signed = asUtc(row.SignedDatetime);
                                const events: Event[] = [];
                                if (signed) {
                                    events.push({at: signed, kind: "PreparedQuote",
                                        label: row.Side === "MANUFACTURER"
                                            ? "Quote response countersigned"
                                            : "Quote response accepted",
                                        id: offer.ObjectID, detail: null, who: row.PrintedName,
                                        revisionUSID: offer.PreparedQuoteRevisionUSID,
                                        key: `${row.USID}:signed`});
                                }
                                const withdrawn = asUtc(row.WithdrawnDatetime);
                                if (withdrawn) {
                                    events.push({at: withdrawn, kind: "PreparedQuote",
                                        label: "Acceptance withdrawn", id: offer.ObjectID,
                                        detail: row.WithdrawnReason ?? null, who: row.PrintedName,
                                        revisionUSID: offer.PreparedQuoteRevisionUSID,
                                        key: `${row.USID}:withdrawn`});
                                }
                                return events;
                            }))))
                            .then(nested => out.concat(...nested));
                    });
            });

        visible.forEach(member => {
            const reads: Promise<Event[]>[] = [auditEvents(member)];
            if (member.MemberType === "QuoteRequest") reads.push(requestEvents(member));
            Promise.all(reads)
                .then(parts => {
                    if (!live) return;
                    setByMember(prior => ({...prior, [member.MemberUSID]: parts.flat()}));
                })
                .catch(() => { if (live) setFailed(prior => [...prior, member.MemberUSID]); })
                .finally(() => { if (live) setPending(prior => Math.max(0, prior - 1)); });
        });
        return () => { live = false; };
        // Keyed on the members' identity AND their resolved numbers: the first pass may run before
        // the resolve has supplied ObjectID, and a row with no number is a row nobody can act on.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [memberKey, api]);

    const events = useMemo(() => {
        const seen = new Set<string>();
        return members
            .flatMap(one => byMember[one.MemberUSID] ?? [])
            .filter(event => !seen.has(event.key) && seen.add(event.key))
            .sort((a, b) => a.at.getTime() - b.at.getTime());
    }, [members, byMember]);

    const hidden = members.filter(one => one.visible === false).length;

    return (
        <Box sx={{maxWidth: 980}}>
            {pending > 0 && (
                <Box sx={{display: "flex", alignItems: "center", gap: "8px", paddingBottom: "8px"}}>
                    <CircularProgress size={14}/>
                    <Typography sx={{fontSize: 12, color: Grey7}}>
                        Reading {pending} record{pending === 1 ? "" : "s"}…
                    </Typography>
                </Box>
            )}
            {events.length === 0 && pending === 0 && (
                <Typography sx={{fontSize: 13, color: Grey7, lineHeight: 1.6}}>
                    Nothing has happened in this project yet. File a record, or log a quote
                    request, and it appears here with its date.
                </Typography>
            )}
            <Box sx={{borderLeft: events.length ? `2px solid ${Grey2}` : "none",
                      marginLeft: "101px", paddingLeft: 0, position: "relative"}}>
                <Box sx={{marginLeft: "-101px"}}>
                    {events.map(event => <Row key={event.key} event={event}/>)}
                </Box>
            </Box>
            {(failed.length > 0 || hidden > 0) && (
                <Typography sx={{fontSize: 11.5, color: Grey7, paddingTop: "10px"}}>
                    {hidden > 0 && `${hidden} record${hidden === 1 ? "" : "s"} filed here ${hidden === 1 ? "is" : "are"} not visible to you and ${hidden === 1 ? "has" : "have"} no rows above. `}
                    {failed.length > 0 && `${failed.length} record${failed.length === 1 ? "'s" : "s'"} history could not be read.`}
                </Typography>
            )}
        </Box>
    );
}
